/**
 * A sandboxed animated figure.
 *
 * The iframe is the security boundary and its attributes are the load-bearing
 * part of this file. `allow-scripts` without `allow-same-origin` is what puts
 * the sketch at an opaque origin; granting both would let the document reach
 * into this page and remove its own sandbox. The server sends the same
 * restriction as a CSP header, so neither lock depends on the other.
 */

import { useEffect, useMemo, useRef } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import { useTheme } from "../theme.tsx";
import { animTheme } from "./theme.ts";
import { useAnimFrame, SANDBOX_URL } from "./useAnimFrame.ts";
import { useReducedMotion } from "./useReducedMotion.ts";
import { MathText } from "../markdown/MathText.tsx";
import { Button } from "../components/ui/button.tsx";
import type { AnimSpec } from "../../shared/anim-spec.ts";

interface AnimFigureProps {
  readonly spec: AnimSpec;
  readonly index?: number;
}

export function AnimFigure({ spec, index }: AnimFigureProps) {
  const { mode } = useTheme();
  const reducedMotion = useReducedMotion();
  // Identity matters here, not just value: the frame is sent a theme whenever
  // this changes, and a fresh object each render is a postMessage each render.
  const theme = useMemo(() => animTheme(mode), [mode]);

  const { frameRef, status, error, playing, setPlaying, suspend } = useAnimFrame(
    { code: spec.code, theme, reducedMotion },
  );

  const stageRef = useRef<HTMLDivElement>(null);

  /**
   * A figure scrolled past is a figure nobody is watching, and a transcript can
   * hold a dozen of them. Each one left looping is a core spent on nothing.
   */
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;

    const observer = new IntersectionObserver(
      ([entry]) => suspend(!entry?.isIntersecting),
      { rootMargin: "128px" },
    );
    observer.observe(stage);
    return () => observer.disconnect();
  }, [suspend]);

  if (status === "failed") {
    return <AnimError message={error} index={index} />;
  }

  return (
    <figure data-anim="figure" className="my-8">
      {spec.title && (
        <MathText
          className="mb-2 block font-serif text-base text-foreground"
          children={spec.title}
        />
      )}

      <div
        ref={stageRef}
        style={{ aspectRatio: String(spec.aspect) }}
        className="group/anim relative w-full overflow-hidden rounded-lg border bg-[var(--surface)] shadow-[var(--shadow)]"
      >
        <iframe
          ref={frameRef}
          src={SANDBOX_URL}
          title={spec.title ?? "Animated figure"}
          /* Scripts, and nothing else. Never add allow-same-origin. */
          sandbox="allow-scripts"
          referrerPolicy="no-referrer"
          loading="lazy"
          className="h-full w-full border-0"
        />

        <div className="no-print absolute right-1.5 bottom-1.5 flex gap-1 opacity-0 transition-opacity group-hover/anim:opacity-100 focus-within:opacity-100">
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={() => setPlaying(!playing)}
            aria-label={playing ? "Pause figure" : "Play figure"}
            className="bg-background/80 backdrop-blur-sm"
          >
            {playing ? <Pause className="size-3.5" /> : <Play className="size-3.5" />}
          </Button>

          <Button
            variant="ghost"
            size="icon-sm"
            /* A fresh document is the only honest restart: the sketch owns its
               own globals and we cannot know what it left behind. */
            onClick={() => {
              const frame = frameRef.current;
              if (frame) frame.src = SANDBOX_URL;
            }}
            aria-label="Restart figure"
            className="bg-background/80 backdrop-blur-sm"
          >
            <RotateCcw className="size-3.5" />
          </Button>
        </div>
      </div>

      {(spec.caption || index !== undefined) && (
        <figcaption className="mt-3 text-[0.94em] leading-relaxed text-secondary-foreground">
          {index !== undefined && (
            <span className="font-bold text-foreground">Figure {index}.</span>
          )}{" "}
          {spec.caption && <MathText children={spec.caption} />}
        </figcaption>
      )}
    </figure>
  );
}

function AnimError({
  message,
  index,
}: {
  readonly message: string | null;
  readonly index?: number;
}) {
  return (
    <div
      role="alert"
      data-anim="error"
      className="my-4 rounded-r-md border-l-[3px] border-l-warning bg-muted px-4 py-3 font-sans text-sm"
    >
      <p className="font-semibold">
        {index !== undefined ? `Figure ${index}` : "This animation"} couldn&rsquo;t
        run.
      </p>
      {message && (
        <p className="mt-1 text-secondary-foreground">{message}</p>
      )}
    </div>
  );
}
